import { parseBom } from './bom'
import { isKnownGerberName, unzipFile } from './gerber'
import { boardNameFromFile, t } from '../i18n'
import type { BomSummary } from '../types'

/**
 * Folder import is deliberately heuristic: customers export boards in wildly
 * different layouts (`Gerber/`, `Drill/`, one ZIP per board, one folder per
 * board). The scanner looks for the shallowest folders that actually contain
 * layer files, treats obvious layer folders (`Gerber`, `Drill`, `Fabrication`)
 * as part of their parent project, and gives each resulting project its own
 * card instead of flattening everything into one board.
 */

export interface IncomingFile {
  file: File
  path: string
}

export interface ImportGroup {
  name: string
  root: string
  files: File[]
  bom: BomSummary | null
}

export interface ImportScan {
  groups: ImportGroup[]
  /** True when more projects were found than the importer will create at once. */
  truncated: boolean
}

const MAX_SCAN_BYTES = 120 * 1024 * 1024
const MAX_SCAN_FILES = 3000
const MAX_GROUPS = 20
const MAX_ZIP_BYTES = 25 * 1024 * 1024
const MAX_BOM_BYTES = 5 * 1024 * 1024
const MAX_TEXT_PROBE_BYTES = 2 * 1024 * 1024

const LAYER_FOLDER_NAMES = new Set([
  'gerber', 'gerbers', 'gerber files', 'gerberfiles', 'artwork', 'artworks',
  'cam', 'output', 'outputs', 'output files', 'fab', 'fabrication',
  'manufacturing', 'drill', 'drills', 'nc drill', 'ncdrill', 'nc drills',
  'pcb', 'pcb files', 'production', 'plots', 'photoplot', 'photoplots',
  '光绘', 'gerber文件', '生产', '输出', '钻孔', '钻孔文件', '制造',
])

const BOM_STOP_WORDS = new Set([
  'bom', 'bill', 'of', 'material', 'materials', 'parts', 'part', 'list',
  'pcb', 'pcba', 'gerber', 'gerbers', 'csv', 'xls', 'xlsx', 'sheet',
  'converted', 'copy', 'final', 'rev', 'revision', 'version', 'the',
])

interface Entry {
  path: string
  name: string
  file: File
  size: number
  text?: string
  candidate?: boolean
}

interface CsvEntry {
  path: string
  name: string
  summary: BomSummary
}

function normalizePath(value: string): string {
  return value
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
}

function baseName(path: string): string {
  const clean = path.replace(/\/+$/, '')
  const slash = clean.lastIndexOf('/')
  return slash >= 0 ? clean.slice(slash + 1) : clean
}

function dirName(path: string): string {
  const slash = path.lastIndexOf('/')
  return slash > 0 ? path.slice(0, slash) : ''
}

function isCsv(name: string): boolean {
  return name.toLowerCase().endsWith('.csv')
}

function isJunk(path: string): boolean {
  const lower = path.toLowerCase()
  const name = baseName(lower)
  return (
    !name ||
    name.startsWith('.') ||
    name === 'thumbs.db' ||
    name === 'desktop.ini' ||
    lower.includes('__macosx/') ||
    /\.(png|jpe?g|gif|webp|bmp|tiff?|pdf|docx?|xlsx?|pptx?|zip|rar|7z|exe|dll|dxf|dwg|step|stp|igs|iges|mp4|mov)$/i.test(name)
  )
}

function looksLikeGerberText(text: string): boolean {
  const head = text.slice(0, 1600)
  return (
    head.includes('%FS') ||
    head.includes('%MO') ||
    head.includes('%ADD') ||
    /^G04/m.test(head) ||
    /^M48/m.test(head) ||
    /^T\d+C/m.test(head)
  )
}

async function collectEntries(inputs: IncomingFile[]): Promise<Entry[]> {
  const entries: Entry[] = []
  let totalBytes = 0

  for (const input of inputs) {
    const path = normalizePath(input.path || input.file.name)
    if (!path) continue
    const lower = path.toLowerCase()

    if (lower.endsWith('.zip')) {
      if (input.file.size > MAX_ZIP_BYTES) {
        throw new Error(t('error.tooLarge', { mb: Math.round(MAX_ZIP_BYTES / 1024 / 1024) }))
      }
      const unzipped = await unzipFile(input.file)
      for (const entry of unzipped) {
        const inner = normalizePath(entry.path)
        const full = `${path.replace(/\.zip$/i, '')}/${inner}`
        if (!inner || isJunk(full)) continue
        const name = baseName(inner)
        entries.push({
          path: full,
          name,
          file: new File([entry.text], name, { type: 'text/plain' }),
          size: entry.size,
          text: entry.text,
        })
        totalBytes += entry.size
        if (entries.length > MAX_SCAN_FILES) {
          throw new Error(t('error.folderTooMany', { n: MAX_SCAN_FILES }))
        }
      }
      continue
    }

    if (isJunk(path)) continue
    entries.push({ path, name: input.file.name, file: input.file, size: input.file.size })
    totalBytes += input.file.size
    if (entries.length > MAX_SCAN_FILES) {
      throw new Error(t('error.folderTooMany', { n: MAX_SCAN_FILES }))
    }
  }

  if (totalBytes > MAX_SCAN_BYTES) {
    throw new Error(t('error.folderTooLarge', { mb: Math.round(MAX_SCAN_BYTES / 1024 / 1024) }))
  }

  // A .txt file is only a layer when it really contains drill/Gerber data;
  // otherwise a README at the folder root would merge every project.
  for (const entry of entries) {
    if (isCsv(entry.name)) continue
    if (!isKnownGerberName(entry.name)) continue
    if (entry.name.toLowerCase().endsWith('.txt')) {
      if (entry.size > MAX_TEXT_PROBE_BYTES) continue
      if (entry.text === undefined) {
        try {
          entry.text = await entry.file.text()
        } catch {
          continue
        }
      }
      entry.candidate = looksLikeGerberText(entry.text)
    } else {
      entry.candidate = true
    }
  }

  return entries
}

function commonDir(paths: string[]): string {
  if (paths.length === 0) return ''
  const split = paths.map((path) => path.split('/').filter(Boolean))
  const first = split[0]
  let depth = 0
  while (depth < first.length && split.every((parts) => parts[depth] === first[depth])) {
    depth += 1
  }
  return first.slice(0, depth).join('/')
}

function childDirs(candidates: Entry[], root: string): string[] {
  const prefix = root ? `${root}/` : ''
  const children = new Set<string>()
  for (const entry of candidates) {
    if (!entry.path.startsWith(prefix)) continue
    const rest = entry.path.slice(prefix.length)
    const slash = rest.indexOf('/')
    if (slash <= 0) continue
    children.add(prefix + rest.slice(0, slash))
  }
  return [...children]
}

function findRoots(candidates: Entry[], root: string): string[] {
  const direct = candidates.some((entry) => dirName(entry.path) === root)
  if (direct) return [root]

  const children = childDirs(candidates, root)
  if (children.length === 0) return [root]
  if (children.length === 1) return findRoots(candidates, children[0])

  // `Project/Gerber/*.gtl` + `Project/Drill/*.drl` is one board, not two.
  if (children.every((child) => LAYER_FOLDER_NAMES.has(baseName(child).toLowerCase()))) {
    return [root]
  }

  return children.flatMap((child) => findRoots(candidates, child))
}

function nameRoot(root: string): string {
  let current = root
  while (current) {
    const leaf = baseName(current).toLowerCase()
    if (!LAYER_FOLDER_NAMES.has(leaf) && !/^\d+$/.test(leaf)) break
    current = dirName(current)
  }
  return current
}

function projectName(root: string, entries: Entry[]): string {
  const leaf = baseName(nameRoot(root))
  const source = leaf || entries[0]?.name.replace(/\.[^.]+$/, '') || ''
  return boardNameFromFile(source)
}

function belongs(path: string, root: string): boolean {
  return root === '' || path === root || path.startsWith(`${root}/`)
}

function normalizeBomName(value: string): string {
  return value
    .toLowerCase()
    .replace(/\.(csv|xlsx?)$/i, '')
    .replace(/[^a-z0-9]+/g, '')
}

function bomTokens(value: string): Set<string> {
  return new Set(
    value
      .toLowerCase()
      .replace(/\.(csv|xlsx?)$/i, '')
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 1 && !BOM_STOP_WORDS.has(token)),
  )
}

function nameSimilarity(project: string, file: string): number {
  const a = normalizeBomName(project)
  const b = normalizeBomName(file)
  if (!a || !b) return 0
  if (b.includes(a) || a.includes(b)) return 24
  let score = 0
  const at = bomTokens(project)
  const bt = bomTokens(file)
  for (const token of at) {
    if (bt.has(token)) score += 8
  }
  return score
}

async function readCsvEntries(entries: Entry[]): Promise<CsvEntry[]> {
  const csvs: CsvEntry[] = []
  for (const entry of entries) {
    if (!isCsv(entry.name) || entry.size > MAX_BOM_BYTES) continue
    let text = entry.text
    if (text === undefined) {
      try {
        text = await entry.file.text()
      } catch {
        continue
      }
    }
    try {
      const summary = parseBom(text, entry.name)
      if (summary.rows < 2 || (summary.uniqueParts === 0 && summary.placements === 0)) continue
      csvs.push({ path: entry.path, name: entry.name, summary })
    } catch {
      // Not a BOM; another CSV in the folder (e.g. a drill report) is fine.
    }
  }
  return csvs
}

function pickBom(csvs: CsvEntry[], root: string, project: string): BomSummary | null {
  if (csvs.length === 0) return null
  const local = csvs.filter((csv) => belongs(csv.path, root))
  const pool = local.length > 0 ? local : csvs.filter((csv) => nameSimilarity(project, csv.name) > 0)
  if (pool.length === 0) return null

  let best: CsvEntry | null = null
  let bestScore = Number.NEGATIVE_INFINITY
  for (const csv of pool) {
    const localBonus = local.includes(csv) ? 60 : 0
    const score =
      localBonus +
      nameSimilarity(project, csv.name) +
      Math.min(20, csv.summary.uniqueParts * 0.4) +
      Math.min(10, csv.summary.placements * 0.02)
    if (score > bestScore) {
      best = csv
      bestScore = score
    }
  }
  return best?.summary ?? null
}

export async function scanIncoming(inputs: IncomingFile[]): Promise<ImportScan> {
  const entries = await collectEntries(inputs)
  const candidates = entries.filter((entry) => entry.candidate)

  if (candidates.length === 0) throw new Error(t('error.noGerber'))

  const common = commonDir(candidates.map((entry) => dirName(entry.path)))
  const roots = [...new Set(findRoots(candidates, common))]
  const ranked = roots
    .map((root) => ({
      root,
      candidates: candidates.filter((entry) => belongs(entry.path, root)),
      entries: entries.filter((entry) => !isCsv(entry.name) && belongs(entry.path, root)),
    }))
    .filter((group) => group.candidates.length > 0)
    .sort((a, b) => b.candidates.length - a.candidates.length || a.root.localeCompare(b.root))

  const truncated = ranked.length > MAX_GROUPS
  const selected = ranked.slice(0, MAX_GROUPS)
  const csvs = await readCsvEntries(entries.filter((entry) => isCsv(entry.name)))

  const groups = selected.map(({ root, entries: groupEntries }) => {
    const name = projectName(root, groupEntries)
    return {
      name,
      root,
      files: groupEntries.map((entry) => entry.file),
      bom: pickBom(csvs, root, name),
    }
  })

  return { groups, truncated }
}

/**
 * Converts a drag/drop or directory-picker batch into paths. `DataTransfer.files`
 * alone drops nested folder contents, so directory items are walked recursively.
 */
export async function filesFromDataTransfer(dataTransfer: DataTransfer): Promise<IncomingFile[]> {
  const items = Array.from(dataTransfer.items ?? [])
  const roots = items
    .map((item) => item.webkitGetAsEntry?.() ?? null)
    .filter((entry): entry is FileSystemEntry => Boolean(entry))

  if (roots.length === 0) {
    return Array.from(dataTransfer.files).map((file) => ({ file, path: file.name }))
  }

  const out: IncomingFile[] = []

  const readDirectory = async (entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> => {
    const reader = entry.createReader()
    const all: FileSystemEntry[] = []
    while (true) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
      if (batch.length === 0) break
      all.push(...batch)
    }
    return all
  }

  const walk = async (entry: FileSystemEntry, parent: string): Promise<void> => {
    const path = parent ? `${parent}/${entry.name}` : entry.name
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        ;(entry as FileSystemFileEntry).file(resolve, reject)
      })
      out.push({ file, path })
      return
    }
    if (!entry.isDirectory) return
    const children = await readDirectory(entry as FileSystemDirectoryEntry)
    for (const child of children) await walk(child, path)
  }

  for (const entry of roots) await walk(entry, '')
  return out
}
